import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { useParams, useLocation } from 'react-router-dom';

import {
  InternalPage,
  Beneficiaries,
  Spacer,
  PageNotFound,
} from 'theSource/components';
import { getCoveragePeriod, mapDetailedBenefit } from './utils';
import {
  CarrierLogo,
  PaymentAndCoverage,
  PlanDetails,
  FamilyCovered,
  KeyActions,
  BenefitDetails,
} from './components';
import { api } from 'theSource/utils';
import {
  BenefitsPageResponseDto,
  usePostEnrollCoverageQuery,
  useMyIntegrationsQuery,
  BenefitDto,
} from 'theSource/queries';
import { PlanDetailsSectionResponseDto } from './components/PlanDetails/types';
import { getBenefitPlanDataQueryKey } from '../utils';
import { BackLink } from 'enrollment/components';

export interface BenefitReviewPageProps {
  data?: BenefitsPageResponseDto;
}

export interface BenefitReviewState {
  benefit: BenefitDto;
}

export const BenefitReviewPage = ({ data }: BenefitReviewPageProps) => {
  const { benefitLookupCode } = useParams();
  const { t } = useTranslation();

  const state = useLocation().state as BenefitReviewState;
  const contextBenefit = state?.benefit;

  let benefits = [];

  if (contextBenefit) {
    benefits.push(contextBenefit);
  } else {
    benefits = data?.data.current as BenefitDto[];
  }

  const benefit =
    data &&
    mapDetailedBenefit(
      benefits,
      data?.data.org_informational_benefits,
      benefitLookupCode ?? ''
    );

  const {
    pageContent: postEnrollCoverage,
    isLoadingPageDetails,
    isFetchingPageDetails,
  } = usePostEnrollCoverageQuery({
    lookupCode: 'post_enroll_coverage',
    coverageId: benefit?.coverageId,
    orgInfoBenId: benefit?.orgInfoBenId,
  });

  const {
    data: planData,
    isLoading: isLoadingPlanDetails,
    isFetching: isFetchingPlanDetails,
  } = useQuery<PlanDetailsSectionResponseDto>(
    getBenefitPlanDataQueryKey(benefit?.orgPlanId as number),
    () =>
      api.get<PlanDetailsSectionResponseDto>(`/org_plan/${benefit?.orgPlanId}`),
    { enabled: Boolean(benefit?.orgPlanId) }
  );

  const {
    integrationsResponse,
    isLoadingIntegrations,
    isFetchingIntegrations,
  } = useMyIntegrationsQuery();
  const integrations = integrationsResponse?.data;

  const isLoading =
    isLoadingPlanDetails ||
    isFetchingPlanDetails ||
    isLoadingPageDetails ||
    isFetchingPageDetails ||
    isLoadingIntegrations ||
    isFetchingIntegrations;

  if (!benefitLookupCode || (!benefit?.orgPlanId && !benefit?.orgInfoBenId)) {
    return <PageNotFound />;
  }

  return (
    <>
      <BackLink
        backLinkValue={t('common:back')}
        internal
        underline={false}
        testId="back-button"
        className="mb-3"
      />
      <InternalPage
        testId="review-benefit-page"
        title={t('pages.benefits:reviewPageTitle')}
        description={benefit?.name}
        descriptionAsHeading
        isLoading={isLoading}
      >
        <div>
          {benefit?.logoUrl ? (
            <CarrierLogo
              name={benefit?.name || t('defaultLogoAltText')}
              url={benefit?.logoUrl}
            />
          ) : null}
        </div>
        {benefit?.orgPlanId ? (
          <>
            <Spacer height={4} />
            <PaymentAndCoverage
              perPayPeriodValue={t('common:numberWithOptions', {
                value: benefit?.paymentSize,
              })}
              coverageFromTo={getCoveragePeriod(
                benefit?.coverageFrom,
                benefit?.coverageTo,
                t('pages.benefits:dateDivider')
              )}
              hideEmptyCost={benefit?.hideEmptyCost}
            />
            <Spacer height={8} />
            <FamilyCovered familyMembers={benefit?.familyMembers} />
            <Beneficiaries beneficiaries={benefit?.beneficiaries} />
            <KeyActions
              keyActions={postEnrollCoverage}
              integrations={integrations}
              carrierLookupCode={benefit?.carrierLookupCode}
            />
            <BenefitDetails
              carrierRequired={false}
              benefitDetails={postEnrollCoverage}
              benefitName={benefit?.name}
            />
            <BenefitDetails
              carrierRequired={true}
              benefitDetails={postEnrollCoverage}
              benefitName={benefit?.name}
            />
            <PlanDetails planData={planData} detailedBenefit={benefit} />
          </>
        ) : (
          <>
            <Spacer height={4} />
            <KeyActions
              keyActions={postEnrollCoverage}
              carrierLookupCode={benefit?.carrierLookupCode}
            />
            <BenefitDetails
              carrierRequired={false}
              benefitDetails={postEnrollCoverage}
              benefitName={benefit?.name}
            />
            <BenefitDetails
              carrierRequired={true}
              benefitDetails={postEnrollCoverage}
              benefitName={benefit?.name}
            />
          </>
        )}
      </InternalPage>
    </>
  );
};
